import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { getErrorMessage } from '../../api/errors';
import { ORDER_STATUS_LABELS } from '../../context/OrderContext';
import { ordersService } from '../../services';
import type { Order, OrderStatus } from '../../types';
import { formatAmount } from '../../utils/formatPrice';
import { formatOrderNumber } from '../../utils/formatOrderNumber';
import {
  formatAddressLine,
  isOrderBillingSameAsShipping,
  resolveOrderBillingAddress,
} from '../../utils/address';
import { resolveProductImageUrl } from '../../utils/product';
import {
  ORDER_REQUEST_STATUS_LABELS,
  ORDER_REQUEST_TYPE_LABELS,
  canAdminCancelOrder,
  getPendingCancellationRequest,
} from '../../utils/orderRequests';

const STATUS_OPTIONS: OrderStatus[] = ['pending', 'processing', 'shipped', 'delivered'];
const FILTER_STATUS_OPTIONS: OrderStatus[] = [...STATUS_OPTIONS, 'cancelled'];
const INVOICE_MAX_BYTES = 10 * 1024 * 1024;
const INVOICE_ACCEPT = '.pdf,.png,.jpg,.jpeg';
const INVOICE_MIME_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];

type StatusFilter = 'all' | OrderStatus;

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('tr-TR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function shortOrderNumber(order: Pick<Order, 'id' | 'orderNumber'>) {
  return formatOrderNumber(order);
}

function validateInvoiceFile(file: File): string | null {
  if (file.size > INVOICE_MAX_BYTES) {
    return 'Fatura en fazla 10 MB olabilir.';
  }
  if (!INVOICE_MIME_TYPES.includes(file.type)) {
    return 'Yalnızca PDF, PNG veya JPG yükleyebilirsiniz.';
  }
  return null;
}

function patchOrder(list: Order[], updated: Order): Order[] {
  return list.map((order) => (order.id === updated.id ? updated : order));
}

function normalizeSearchText(value: string) {
  return value.trim().toLocaleLowerCase('tr-TR');
}

function orderMatchesSearch(order: Order, query: string) {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return true;

  const billingAddress = resolveOrderBillingAddress(order);
  const haystack = [
    order.id,
    String(order.orderNumber ?? ''),
    order.contact.name,
    order.contact.email,
    order.contact.phone,
    order.contact.identityNumber ?? '',
    order.trackingNumber ?? '',
    order.shippingCarrierName ?? '',
    order.paymentReference ?? '',
    order.couponCode ?? '',
    order.shippingAddress.address,
    order.shippingAddress.city,
    order.shippingAddress.district,
    order.shippingAddress.zip,
    billingAddress.address,
    billingAddress.city,
    billingAddress.district,
    billingAddress.zip,
    ORDER_STATUS_LABELS[order.status],
    ...order.items.flatMap((item) => [item.productName, item.productId]),
  ]
    .map((value) => String(value).toLocaleLowerCase('tr-TR'))
    .join(' ');

  return haystack.includes(normalizedQuery);
}

export default function AdminOrders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [savingTrackingId, setSavingTrackingId] = useState<string | null>(null);
  const [uploadingInvoiceId, setUploadingInvoiceId] = useState<string | null>(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [trackingDrafts, setTrackingDrafts] = useState<Record<string, string>>({});
  const invoiceInputRef = useRef<HTMLInputElement>(null);
  const [invoiceTargetId, setInvoiceTargetId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelNoteDrafts, setCancelNoteDrafts] = useState<Record<string, string>>({});
  const [cancelDialogOrderId, setCancelDialogOrderId] = useState<string | null>(null);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await ordersService.fetchAllOrders();
      setOrders(data);
    } catch (err) {
      setError(getErrorMessage(err, 'Siparişler yüklenemedi.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (statusFilter !== 'all' && order.status !== statusFilter) return false;
      return orderMatchesSearch(order, searchQuery);
    });
  }, [orders, statusFilter, searchQuery]);

  const emptyMessage = useMemo(() => {
    if (orders.length === 0) return 'Henüz sipariş yok.';
    if (searchQuery.trim()) return 'Arama kriterlerine uygun sipariş bulunamadı.';
    if (statusFilter !== 'all') return 'Bu durumda sipariş bulunamadı.';
    return 'Henüz sipariş yok.';
  }, [orders.length, searchQuery, statusFilter]);

  const stats = useMemo(() => {
    const counts: Record<OrderStatus, number> = {
      pending: 0,
      processing: 0,
      shipped: 0,
      delivered: 0,
      cancelled: 0,
    };
    for (const order of orders) {
      counts[order.status] += 1;
    }
    return counts;
  }, [orders]);

  const handleStatusChange = async (orderId: string, status: OrderStatus) => {
    setUpdatingId(orderId);
    setError('');
    try {
      const updated = await ordersService.updateOrderStatus(orderId, status);
      setOrders((prev) => patchOrder(prev, updated));
    } catch (err) {
      setError(getErrorMessage(err, 'Durum güncellenemedi.'));
    } finally {
      setUpdatingId(null);
    }
  };

  const handleSaveTracking = async (orderId: string) => {
    const draft = trackingDrafts[orderId]?.trim() ?? '';
    setSavingTrackingId(orderId);
    setError('');
    try {
      const updated = await ordersService.updateOrderTracking(orderId, draft || null);
      setOrders((prev) => patchOrder(prev, updated));
      setTrackingDrafts((prev) => ({ ...prev, [orderId]: updated.trackingNumber ?? '' }));
    } catch (err) {
      setError(getErrorMessage(err, 'Kargo numarası kaydedilemedi.'));
    } finally {
      setSavingTrackingId(null);
    }
  };

  const handleInvoicePick = (orderId: string) => {
    setInvoiceTargetId(orderId);
    invoiceInputRef.current?.click();
  };

  const handleInvoiceFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const orderId = invoiceTargetId;
    setInvoiceTargetId(null);
    if (!file || !orderId) return;

    const validationError = validateInvoiceFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploadingInvoiceId(orderId);
    setError('');
    try {
      const updated = await ordersService.uploadAdminOrderInvoice(orderId, file);
      setOrders((prev) => patchOrder(prev, updated));
    } catch (err) {
      setError(getErrorMessage(err, 'Fatura yüklenemedi.'));
    } finally {
      setUploadingInvoiceId(null);
    }
  };

  const handleDownloadInvoice = async (orderId: string) => {
    setDownloadingInvoiceId(orderId);
    setError('');
    try {
      await ordersService.downloadAdminOrderInvoice(orderId);
    } catch (err) {
      setError(getErrorMessage(err, 'Fatura indirilemedi.'));
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const handleAdminCancel = async (orderId: string) => {
    const order = orders.find((item) => item.id === orderId);
    if (!order || !canAdminCancelOrder(order)) return;

    const note = cancelNoteDrafts[orderId]?.trim();
    setCancellingId(orderId);
    setError('');
    try {
      const updated = await ordersService.adminCancelOrder(orderId, note || null);
      setOrders((prev) => patchOrder(prev, updated));
      setCancelDialogOrderId(null);
      setCancelNoteDrafts((prev) => ({ ...prev, [orderId]: '' }));
      if (expandedId === orderId) {
        setExpandedId(null);
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Sipariş iptal edilemedi.'));
    } finally {
      setCancellingId(null);
    }
  };

  const openCancelDialog = (orderId: string) => {
    setCancelDialogOrderId(orderId);
  };

  const closeCancelDialog = () => {
    if (cancellingId) return;
    setCancelDialogOrderId(null);
  };

  const toggleExpanded = (orderId: string) => {
    setExpandedId((current) => {
      if (current === orderId) return null;
      const order = orders.find((item) => item.id === orderId);
      if (order) {
        setTrackingDrafts((prev) => ({
          ...prev,
          [orderId]: order.trackingNumber ?? '',
        }));
      }
      return orderId;
    });
  };

  return (
    <div className="admin-page">
      <input
        ref={invoiceInputRef}
        type="file"
        accept={INVOICE_ACCEPT}
        className="sr-only"
        onChange={(event) => void handleInvoiceFileChange(event)}
      />
      <div className="admin-page-header">
        <div>
          <h1>Siparişler</h1>
          <p>Tüm müşteri siparişlerini görüntüleyin, durum güncelleyin veya doğrudan iptal edin</p>
        </div>
        <button type="button" className="btn btn-outline" onClick={() => void loadOrders()}>
          Yenile
        </button>
      </div>

      <div className="admin-stats admin-order-stats">
        <div className="admin-stat-card">
          <span>Toplam</span>
          <strong>{orders.length}</strong>
        </div>
        {STATUS_OPTIONS.map((status) => (
          <div key={status} className="admin-stat-card">
            <span>{ORDER_STATUS_LABELS[status]}</span>
            <strong>{stats[status]}</strong>
          </div>
        ))}
        <div className="admin-stat-card">
          <span>{ORDER_STATUS_LABELS.cancelled}</span>
          <strong>{stats.cancelled}</strong>
        </div>
      </div>

      <div className="admin-order-filters">
        <label htmlFor="orderSearch">Ara</label>
        <input
          id="orderSearch"
          type="search"
          className="search-input admin-order-search"
          placeholder="Sipariş no, müşteri, e-posta, telefon, kargo no..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <label htmlFor="statusFilter">Durum</label>
        <select
          id="statusFilter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
        >
          <option value="all">Tümü ({orders.length})</option>
          {FILTER_STATUS_OPTIONS.map((status) => (
            <option key={status} value={status}>
              {ORDER_STATUS_LABELS[status]} ({stats[status]})
            </option>
          ))}
        </select>
        {(searchQuery.trim() || statusFilter !== 'all') && (
          <span className="admin-order-filter-count">
            {filteredOrders.length} sonuç
          </span>
        )}
      </div>

      {error && (
        <div className="auth-error">
          {error}
          <button type="button" className="btn btn-outline btn-sm" onClick={() => void loadOrders()}>
            Tekrar Dene
          </button>
        </div>
      )}

      {isLoading ? (
        <p>Yükleniyor...</p>
      ) : filteredOrders.length === 0 ? (
        <div className="admin-empty">
          <p>{emptyMessage}</p>
          {(searchQuery.trim() || statusFilter !== 'all') && orders.length > 0 && (
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
              }}
            >
              Filtreleri Temizle
            </button>
          )}
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table admin-orders-table">
            <thead>
              <tr>
                <th>Sipariş</th>
                <th>Müşteri</th>
                <th>Tutar</th>
                <th>Durum</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((order) => {
                const isExpanded = expandedId === order.id;
                const isUpdating = updatingId === order.id;
                const pendingCancellation = getPendingCancellationRequest(order);
                const isCancelled = order.status === 'cancelled';

                return (
                  <Fragment key={order.id}>
                    <tr>
                      <td>
                        <div className="admin-order-id-cell">
                          <strong>{shortOrderNumber(order)}</strong>
                          <span>{formatDate(order.createdAt)}</span>
                          <span>{order.items.length} ürün</span>
                        </div>
                      </td>
                      <td>
                        <div className="admin-order-customer">
                          <strong>{order.contact.name}</strong>
                          <span>{order.contact.email}</span>
                          <span>{order.contact.phone}</span>
                        </div>
                      </td>
                      <td>
                        <strong>{formatAmount(order.total, order.currency)}</strong>
                        {order.shipping > 0 && (
                          <span className="admin-order-shipping">
                            Kargo: {formatAmount(order.shipping, order.currency)}
                          </span>
                        )}
                      </td>
                      <td>
                        {isCancelled ? (
                          <span className={`order-status status-${order.status}`}>
                            {ORDER_STATUS_LABELS[order.status]}
                          </span>
                        ) : (
                          <select
                            className={`admin-status-select status-${order.status}`}
                            value={order.status}
                            disabled={isUpdating}
                            onChange={(e) =>
                              void handleStatusChange(order.id, e.target.value as OrderStatus)
                            }
                          >
                            {STATUS_OPTIONS.map((status) => (
                              <option key={status} value={status}>
                                {ORDER_STATUS_LABELS[status]}
                              </option>
                            ))}
                          </select>
                        )}
                        {pendingCancellation && (
                          <span className="admin-order-request-badge">
                            İptal talebi bekliyor
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="admin-order-actions">
                          <button
                            type="button"
                            className="btn btn-outline btn-sm"
                            onClick={() => toggleExpanded(order.id)}
                          >
                            {isExpanded ? 'Gizle' : 'Detay'}
                          </button>
                          {canAdminCancelOrder(order) && (
                            <button
                              type="button"
                              className="btn btn-outline btn-sm admin-order-cancel-btn"
                              disabled={cancellingId === order.id}
                              onClick={() => openCancelDialog(order.id)}
                            >
                              İptal Et
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="admin-order-detail-row">
                        <td colSpan={5}>
                          <div className="admin-order-detail">
                            <div className="admin-order-detail-section">
                              <h3>Ürünler</h3>
                              <ul className="admin-order-items">
                                {order.items.map((item) => (
                                  <li key={item.productId}>
                                    <img src={resolveProductImageUrl(item.productImage)} alt={item.productName} />
                                    <div>
                                      <strong>{item.productName}</strong>
                                      <span>
                                        {item.quantity} adet ×{' '}
                                        {formatAmount(item.price, item.currency)}
                                      </span>
                                    </div>
                                    <span>
                                      {formatAmount(item.price * item.quantity, item.currency)}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div className="admin-order-detail-section">
                              <h3>İletişim</h3>
                              <dl className="admin-order-summary">
                                <div>
                                  <dt>Ad Soyad</dt>
                                  <dd>{order.contact.name}</dd>
                                </div>
                                <div>
                                  <dt>E-posta</dt>
                                  <dd>{order.contact.email}</dd>
                                </div>
                                <div>
                                  <dt>Telefon</dt>
                                  <dd>{order.contact.phone}</dd>
                                </div>
                                <div>
                                  <dt>TC / Vergi Kimlik No</dt>
                                  <dd>
                                    {order.contact.identityNumber?.trim()
                                      ? order.contact.identityNumber
                                      : '—'}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                            <div className="admin-order-detail-section">
                              <h3>Teslimat Adresi</h3>
                              <p>{formatAddressLine(order.shippingAddress)}</p>
                            </div>
                            <div className="admin-order-detail-section">
                              <h3>Fatura Adresi</h3>
                              <p>{formatAddressLine(resolveOrderBillingAddress(order))}</p>
                              {isOrderBillingSameAsShipping(order) && (
                                <p className="admin-order-meta">Teslimat adresi ile aynı</p>
                              )}
                            </div>
                            <div className="admin-order-detail-section">
                              <h3>Özet</h3>
                              <dl className="admin-order-summary">
                                <div>
                                  <dt>Ara Toplam</dt>
                                  <dd>{formatAmount(order.subtotal, order.currency)}</dd>
                                </div>
                                {(order.discountAmount ?? 0) > 0 && (
                                  <div>
                                    <dt>İndirim{order.couponCode ? ` (${order.couponCode})` : ''}</dt>
                                    <dd>−{formatAmount(order.discountAmount ?? 0, order.currency)}</dd>
                                  </div>
                                )}
                                <div>
                                  <dt>Kargo</dt>
                                  <dd>
                                    {order.shipping === 0
                                      ? 'Ücretsiz'
                                      : formatAmount(order.shipping, order.currency)}
                                  </dd>
                                </div>
                                <div>
                                  <dt>Toplam</dt>
                                  <dd>{formatAmount(order.total, order.currency)}</dd>
                                </div>
                              </dl>
                            </div>
                            <div className="admin-order-detail-section admin-order-fulfillment">
                              <h3>Kargo &amp; Fatura</h3>
                              {order.shippingCarrierName && (
                                <p className="admin-order-meta">
                                  Kargo firması: {order.shippingCarrierName}
                                </p>
                              )}
                              <div className="admin-order-tracking">
                                <label htmlFor={`tracking-${order.id}`}>Kargo takip no</label>
                                <div className="admin-order-tracking-row">
                                  <input
                                    id={`tracking-${order.id}`}
                                    type="text"
                                    value={trackingDrafts[order.id] ?? order.trackingNumber ?? ''}
                                    placeholder="1234567890TR"
                                    disabled={savingTrackingId === order.id}
                                    onChange={(event) =>
                                      setTrackingDrafts((prev) => ({
                                        ...prev,
                                        [order.id]: event.target.value,
                                      }))
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    disabled={savingTrackingId === order.id}
                                    onClick={() => void handleSaveTracking(order.id)}
                                  >
                                    {savingTrackingId === order.id ? 'Kaydediliyor...' : 'Kaydet'}
                                  </button>
                                </div>
                                {order.trackingNumber && (
                                  <p className="admin-order-meta">
                                    Kayıtlı:{' '}
                                    {order.shippingCarrierName
                                      ? `${order.shippingCarrierName} — ${order.trackingNumber}`
                                      : order.trackingNumber}
                                  </p>
                                )}
                              </div>
                              <div className="admin-order-invoice">
                                <p className="admin-order-meta">
                                  Fatura: {order.invoiceAvailable ? 'Yüklü' : 'Henüz yüklenmedi'}
                                </p>
                                <div className="admin-order-invoice-actions">
                                  <button
                                    type="button"
                                    className="btn btn-outline btn-sm"
                                    disabled={uploadingInvoiceId === order.id}
                                    onClick={() => handleInvoicePick(order.id)}
                                  >
                                    {uploadingInvoiceId === order.id ? 'Yükleniyor...' : 'Fatura Yükle'}
                                  </button>
                                  {order.invoiceAvailable && (
                                    <button
                                      type="button"
                                      className="btn btn-outline btn-sm"
                                      disabled={downloadingInvoiceId === order.id}
                                      onClick={() => void handleDownloadInvoice(order.id)}
                                    >
                                      {downloadingInvoiceId === order.id ? 'İndiriliyor...' : 'Faturayı İndir'}
                                    </button>
                                  )}
                                </div>
                                <p className="admin-order-hint">PDF, PNG veya JPG — en fazla 10 MB</p>
                              </div>
                            </div>
                            {(order.requests?.length ?? 0) > 0 && (
                              <div className="admin-order-detail-section">
                                <h3>İptal / İade Talepleri</h3>
                                <ul className="admin-order-requests">
                                  {order.requests!.map((request) => (
                                    <li key={request.id}>
                                      <strong>
                                        {ORDER_REQUEST_TYPE_LABELS[request.type]} —{' '}
                                        {ORDER_REQUEST_STATUS_LABELS[request.status]}
                                      </strong>
                                      <span>{formatDate(request.createdAt)}</span>
                                      <p>{request.reason}</p>
                                      {request.adminNote && (
                                        <p className="admin-order-meta">Admin notu: {request.adminNote}</p>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {canAdminCancelOrder(order) && (
                              <div className="admin-order-detail-section admin-order-cancel">
                                <h3>Siparişi İptal Et</h3>
                                <p className="admin-order-meta">
                                  Doğrudan iptal ederseniz stok geri eklenir; bekleyen iptal talebi varsa
                                  otomatik onaylanır.
                                </p>
                                <button
                                  type="button"
                                  className="btn btn-outline btn-sm admin-order-cancel-btn"
                                  disabled={cancellingId === order.id}
                                  onClick={() => openCancelDialog(order.id)}
                                >
                                  Siparişi İptal Et
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {cancelDialogOrderId && (
        <div
          className="admin-dialog-backdrop"
          role="presentation"
          onClick={closeCancelDialog}
        >
          <div
            className="admin-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-cancel-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="admin-cancel-dialog-title">
              {shortOrderNumber(
                orders.find((order) => order.id === cancelDialogOrderId) ?? {
                  id: cancelDialogOrderId,
                  orderNumber: 0,
                },
              )}{' '}
              siparişini iptal et
            </h2>
            <p className="admin-order-meta">
              Stok geri eklenecek. Bekleyen müşteri iptal talebi varsa otomatik onaylanır.
            </p>
            <label htmlFor="admin-cancel-note">Admin notu (opsiyonel)</label>
            <textarea
              id="admin-cancel-note"
              rows={3}
              placeholder="Müşteriye iletilecek açıklama"
              value={cancelNoteDrafts[cancelDialogOrderId] ?? ''}
              disabled={cancellingId === cancelDialogOrderId}
              onChange={(event) =>
                setCancelNoteDrafts((prev) => ({
                  ...prev,
                  [cancelDialogOrderId]: event.target.value,
                }))
              }
            />
            <div className="admin-dialog-actions">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={cancellingId === cancelDialogOrderId}
                onClick={closeCancelDialog}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm admin-order-cancel-btn"
                disabled={cancellingId === cancelDialogOrderId}
                onClick={() => void handleAdminCancel(cancelDialogOrderId)}
              >
                {cancellingId === cancelDialogOrderId ? 'İptal ediliyor...' : 'Siparişi İptal Et'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
